import { useEffect, useRef, useState, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import RealEstatePageLayout from '../../components/partials/RealEstatePageLayout';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import ImageLoader from '../../components/ImageLoader';
import PropertyCard from '../../components/PropertyCard';
import SimpleBar from 'simplebar-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import 'simplebar-react/dist/simplebar.min.css';
import 'leaflet/dist/leaflet.css';
import Spinner from 'react-bootstrap/Spinner';
import Placeholder from 'react-bootstrap/Placeholder';
import Card from 'react-bootstrap/Card';
import { Dropdown } from 'primereact/dropdown';
import { api } from "../../libs/utils/api";
import { listingPath } from '../../libs/utils/seo';

// --- Dynamic Imports for Leaflet (remains the same) ---
const MapContainer = dynamic(() =>
  import('react-leaflet').then(mod => mod.MapContainer),
  { ssr: false }
);
const TileLayer = dynamic(() =>
  import('react-leaflet').then(mod => mod.TileLayer),
  { ssr: false }
);
const CustomMarker = dynamic(() =>
  import('../../components/partials/CustomMarker'),
  { ssr: false }
);
const Popup = dynamic(() =>
  import('react-leaflet').then(mod => mod.Popup),
  { ssr: false }
);

// --- NoData Component (remains the same) ---
const NoData = () => {
  const { t } = useTranslation('common');
  return (
    <div className={'m-auto'}>
      <ImageLoader
        src='/images/illustrations/no-house.jpg'
        width={416}
        height={400}
        alt=''
        className={'rounded'}
      />
      <h5 className={'text-center'}>{t('Aucune annonce trouvée')}</h5>
    </div>
  );
};

// --- Enhanced Loading Component ---
const SkeletonCard = () => {
  return (
    <div className="card border-0 shadow h-100">
      <div className="card-img-top ratio ratio-16x9 bg-light placeholder-wave"></div>
      <div className="card-body">
        <div className="h5 placeholder-glow">
          <span className="placeholder col-8"></span>
        </div>
        <div className="placeholder-glow">
          <span className="placeholder col-6"></span>
        </div>
      </div>
      <div className="card-footer">
        <div className="d-flex justify-content-between placeholder-glow">
          <span className="placeholder col-3"></span>
          <span className="placeholder col-3"></span>
          <span className="placeholder col-3"></span>
        </div>
      </div>
    </div>
  );
};

// --- LoadingCardCatalog Component (remains the same) ---
const LoadingCardCatalog = () => {
  return (
    <Card className='border-0 shadow p-0'>
      <Placeholder as='div' animation='wave' className='position-relative'>
        <Placeholder as='div' className='card-img-top ratio ratio-16x9' />
        <i className='fi-image position-absolute top-50 start-50 translate-middle fs-1 opacity-40'></i>
      </Placeholder>
      <Card.Body>
        <Placeholder as='h3' animation='glow' className='h6 mb-2 fs-base'>
          <Placeholder xs={6} />
        </Placeholder>
        <Placeholder as='p' animation='glow' className='card-text'>
          <Placeholder size='sm' xs={7} className='me-2' />
        </Placeholder>
      </Card.Body>
      <Card.Footer>
        <Placeholder as='p' animation='glow' className='card-text'>
          <Placeholder size='sm' xs={7} className='me-2' />
        </Placeholder>
      </Card.Footer>
    </Card>
  );
};

// --- Active Filters Summary Component ---
const ActiveFiltersSummary = ({
  localCitySelected,
  localNeighbourhoodSelected,
  localPropertyTypes,
  localMinPrice,
  localMaxPrice,
  localBedrooms,
  localBathrooms,
  localMinArea,
  localMaxArea,
  onClearFilters,
  onRemoveFilter,
  t
}) => {
  const activeFilters = [];

  if (localCitySelected) activeFilters.push({ key: 'city', label: `${t('Ville')}: ${localCitySelected.name}`, value: localCitySelected });
  if (localNeighbourhoodSelected) activeFilters.push({ key: 'neighbourhood', label: `${t('Quartier')}: ${localNeighbourhoodSelected.name}`, value: localNeighbourhoodSelected });

  localPropertyTypes.forEach(type => {
    activeFilters.push({ key: 'propertyType', label: type, value: type });
  });

  if (localMinPrice) activeFilters.push({ key: 'minPrice', label: `${t('Min Budget')}: ${localMinPrice} DH`, value: localMinPrice });
  if (localMaxPrice) activeFilters.push({ key: 'maxPrice', label: `${t('Max Budget')}: ${localMaxPrice} DH`, value: localMaxPrice });

  if (localBedrooms) activeFilters.push({ key: 'bedrooms', label: `${t('Chambres')}: ${localBedrooms}`, value: localBedrooms });
  if (localBathrooms) activeFilters.push({ key: 'bathrooms', label: `${t('SDB')}: ${localBathrooms}`, value: localBathrooms });

  if (localMinArea) activeFilters.push({ key: 'minArea', label: `${t('Min Surface')}: ${localMinArea} m²`, value: localMinArea });
  if (localMaxArea) activeFilters.push({ key: 'maxArea', label: `${t('Max Surface')}: ${localMaxArea} m²`, value: localMaxArea });


  if (activeFilters.length === 0) return null;

  return (
    <div className='d-flex align-items-center flex-wrap gap-2 mb-3'>
      <small className='text-muted'>{t('Filtres actifs')}:</small>
      {activeFilters.map((filter, index) => (
        <span key={index} className='badge bg-faded-primary text-primary border d-flex align-items-center'>
          {filter.label}
          <i className='fi-x fs-xs ms-2 cursor-pointer' onClick={() => onRemoveFilter(filter.key, filter.value)}></i>
        </span>
      ))}
      <Button
        variant='link'
        size='sm'
        className='p-0 ms-2 text-decoration-none'
        onClick={onClearFilters}
      >
        {t('Tout effacer')}
      </Button>
    </div>
  );
};

const CatalogPage = ({ initialProperties = [], initialLocations = {}, initialTotalResults = 0 }) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const LIMIT_PER_PAGE = 9;

  // Get current query values (used to initialize local state and track URL)
  const { amenities, minArea, maxArea, bathrooms, bedrooms, minPrice, maxPrice, city, neighbourhood, type_bien, orderBy } = router.query;
  const categoryParam = router.query.category === 'location' ? 'location' : 'vendre';

  // 1. Properties and Pagination State
  const [properties, setProperties] = useState(initialProperties);
  const [loadingProperties, setLoadingProperties] = useState(false);
  // Current page starts at 1 if initial properties were loaded, otherwise based on query
  const [currentPage, setCurrentPage] = useState(parseInt(router.query.page) || 1);
  const [totalResults, setTotalResults] = useState(initialTotalResults);
  const [hasMore, setHasMore] = useState(initialProperties.length === LIMIT_PER_PAGE);

  // 2. Local Filter Form State (Used for inputs, separate from router.query)
  const [localPropertyTypes, setLocalPropertyTypes] = useState(type_bien ? type_bien.split(',') : []);
  const [localAmenities, setLocalAmenities] = useState(amenities ? amenities.split(',') : []);
  const [localMinPrice, setLocalMinPrice] = useState(minPrice || '');
  const [localMaxPrice, setLocalMaxPrice] = useState(maxPrice || '');
  const [localMinArea, setLocalMinArea] = useState(minArea || '');
  const [localMaxArea, setLocalMaxArea] = useState(maxArea || '');
  const [localBedrooms, setLocalBedrooms] = useState(bedrooms || '');
  const [localBathrooms, setLocalBathrooms] = useState(bathrooms || '');
  const [localOrderBy, setLocalOrderBy] = useState(orderBy || 'date');
  const [localCategory, setLocalCategory] = useState(categoryParam);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Initial Location Setup
  const initialCity = initialLocations.cities?.find(item => item?.id_city === parseInt(city));
  const initialNeighbourhood = Object.values(initialLocations.neighbourhoods_by_city || {}).flat().find(item => item?.id_neighborhood === parseInt(neighbourhood));
  const [localCitySelected, setLocalCitySelected] = useState(initialCity || null);
  const [localNeighbourhoodSelected, setLocalNeighbourhoodSelected] = useState(initialNeighbourhood || null);

  // Location Data (from SSR props)
  const [cities] = useState(initialLocations.cities || []);
  const [neighbourhoodsByCity] = useState(initialLocations.neighbourhoods_by_city || {});

  const refDropDownCity = useRef();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [showMap, setShowMap] = useState(false);

  // Utility function to build the query object from current *local* state
  const buildQueryFromLocalState = useCallback((page = 1) => {
    let query = {};

    if (localCitySelected) query.city = localCitySelected.id_city;
    if (localNeighbourhoodSelected) query.neighbourhood = localNeighbourhoodSelected.id_neighborhood;
    if (localPropertyTypes.length > 0) query.type_bien = localPropertyTypes.join(',');
    if (localAmenities.length > 0) query.amenities = localAmenities.join(',');
    if (localMinPrice) query.minPrice = localMinPrice.replace(/\s/g, '');
    if (localMaxPrice) query.maxPrice = localMaxPrice.replace(/\s/g, '');
    if (localMinArea) query.minArea = localMinArea;
    if (localMaxArea) query.maxArea = localMaxArea;
    if (localBedrooms) query.bedrooms = localBedrooms;
    if (localBathrooms) query.bathrooms = localBathrooms;
    if (localOrderBy && localOrderBy !== 'date') query.orderBy = localOrderBy;
    if (localCategory && localCategory !== 'vendre') query.category = localCategory;

    // Add pagination parameters
    query.page = page;
    query.limit = LIMIT_PER_PAGE;

    return query;
  }, [localCitySelected, localNeighbourhoodSelected, localPropertyTypes, localAmenities, localMinPrice, localMaxPrice, localMinArea, localMaxArea, localBedrooms, localBathrooms, localOrderBy, localCategory]);

  // Calculate active filters
  useEffect(() => {
    const query = buildQueryFromLocalState(1);
    const hasFilters = Object.keys(query).some(key =>
      !['page', 'limit', 'category'].includes(key) && query[key]
    );
    const filterCount = Object.keys(query).filter(key =>
      !['page', 'limit', 'category'].includes(key) && query[key]
    ).length;

    setHasActiveFilters(hasFilters);
    setActiveFilterCount(filterCount);
  }, [buildQueryFromLocalState]);

  useEffect(() => {
    const body = document.querySelector('body');
    document.body.classList.add('fixed-bottom-btn');
    return () => body.classList.remove('fixed-bottom-btn');
  }, []);

  // --- State Persistence ---
  useEffect(() => {
    // Restore logic
    const savedState = sessionStorage.getItem('catalogState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        // Only restore if the current URL path matches the saved one (meaning we are back on the exact same page state)
        if (router.asPath === parsedState.path) {
          setProperties(parsedState.properties);
          setCurrentPage(parsedState.currentPage);
          setTotalResults(parsedState.totalResults);
          setHasMore(parsedState.hasMore);
          // Restore scroll position after a slight delay to allow render
          setTimeout(() => {
            window.scrollTo(0, parsedState.scrollY);
          }, 100);
        }
      } catch (e) {
        console.error("Failed to restore catalog state", e);
      }
    }
  }, []); // Run on mount only (to check for saved state)

  useEffect(() => {
    // Save state logic on route change
    const handleRouteChangeStart = (url) => {
      // Save current state
      const stateToSave = {
        properties,
        currentPage,
        totalResults,
        hasMore,
        scrollY: window.scrollY,
        path: router.asPath // Save current path including query params
      };
      sessionStorage.setItem('catalogState', JSON.stringify(stateToSave));
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [properties, currentPage, totalResults, hasMore, router.asPath]); // Re-bind with latest state

  const fetchData = useCallback(async (query, append = false) => {
    const currentCategory = query.category || 'vendre';

    setLoadingProperties(true);
    try {
      const { data: response } = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/api/ads/properties/${currentCategory}`,
        query
      );
      if (!response.success) {
        console.error('API returned error:', response.error);
        if (!append) setProperties([]);
        setHasMore(false);
        return;
      }

      const propertyData = response.data?.properties || [];
      const hasMoreData = response.data?.pagination?.has_more || false;

      setTotalResults(response.data?.pagination?.total || 0);
      if (append) {
        setProperties(prev => [...prev, ...propertyData]);
      } else {
        setProperties(propertyData);
        setCurrentPage(1); // Reset page to 1 on a fresh search
      }

      setHasMore(hasMoreData);

    } catch (error) {
      console.error('Error fetching properties:', error);
      if (!append) setProperties([]);
      setHasMore(false);
    } finally {
      setLoadingProperties(false);
    }
  }, []);

  // Handler for the "Apply Filters" button
  const handleApplyFilters = () => {
    handleClose(); // Close offcanvas on mobile/tablet

    const newQuery = buildQueryFromLocalState(1); // Start at page 1 for new search

    // 1. Update the URL to reflect new filters (Optional, but good for bookmarking)
    router.push({
      pathname: router.pathname,
      query: newQuery,
    }, undefined, { shallow: true });

    // 2. Fetch data for the new query, overwriting old results
    fetchData(newQuery, false);
  };

  // Handler for the "Load More" button
  const handleLoadMore = () => {
    // 1. Calculate the page number for the NEXT request
    const nextPage = currentPage + 1;

    // 2. Build the query using the NEXT page number
    const newQuery = buildQueryFromLocalState(nextPage);

    // 3. Update the client-side state *before* fetching
    setCurrentPage(nextPage);

    // 4. Fetch data (sends nextPage)
    fetchData(newQuery, true);
  };

  // Handler for the sort dropdown
  const handleOrderBy = (event) => {
    setLocalOrderBy(event.target.value);
  };

  // --- Filtering Handlers ---
  const handleAmenities = (e) => {
    const amenity = e.target.value;
    setLocalAmenities(prev => prev.includes(amenity) ? prev.filter(item => item !== amenity) : [...prev, amenity]);
  };

  const handlePropertyType = (e) => {
    const type = e.target.value;
    setLocalPropertyTypes(prev => prev.includes(type) ? prev.filter(item => item !== type) : [...prev, type]);
  };

  const handleCity = (e) => {
    const cityValue = e.value || null;
    setLocalCitySelected(cityValue);
    setLocalNeighbourhoodSelected(null); // Reset neighbourhood
  };

  const handleNeighbourhood = (e) => {
    setLocalNeighbourhoodSelected(e.value || null);
  };

  const handleCategoryBien = (e) => {
    setLocalCategory(e.target.value);
  };

  // Helper to format number with spaces
  const formatPrice = (value) => {
    if (!value) return '';
    // Remove existing spaces and non-digits
    const rawValue = value.replace(/\D/g, '');
    return rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  // Enhanced price handlers with validation
  const handleMinPrice = (e) => {
    const value = e.target.value;
    // Allow digits and spaces
    if (/^[\d\s]*$/.test(value)) {
      setLocalMinPrice(formatPrice(value));
    }
  };

  const handleMaxPrice = (e) => {
    const value = e.target.value;
    // Allow digits and spaces
    if (/^[\d\s]*$/.test(value)) {
      setLocalMaxPrice(formatPrice(value));
    }
  };

  const handleBedrooms = (e) => setLocalBedrooms(e.target.value);
  const handleBathrooms = (e) => setLocalBathrooms(e.target.value);
  const handleMinArea = (e) => setLocalMinArea(e.target.value);
  const handleMaxArea = (e) => setLocalMaxArea(e.target.value);

  // Clear filters function
  // Clear filters function
  // Handler to remove a specific filter
  const handleRemoveFilter = (key, value) => {
    // 1. Get current query based on state before update
    const currentQuery = buildQueryFromLocalState(1);

    // 2. Clear clean formatted price strings for calculation if needed, 
    // but here we just need to identify what to remove from the query object.

    // 3. Update local state and modify query for immediate fetch
    if (key === 'city') {
      setLocalCitySelected(null);
      setLocalNeighbourhoodSelected(null);
      delete currentQuery.city;
      delete currentQuery.neighbourhood;
    } else if (key === 'neighbourhood') {
      setLocalNeighbourhoodSelected(null);
      delete currentQuery.neighbourhood;
    } else if (key === 'propertyType') {
      const newTypes = localPropertyTypes.filter(t => t !== value);
      setLocalPropertyTypes(newTypes);
      if (newTypes.length > 0) {
        currentQuery.type_bien = newTypes.join(',');
      } else {
        delete currentQuery.type_bien;
      }
    } else if (key === 'minPrice') {
      setLocalMinPrice('');
      delete currentQuery.minPrice;
    } else if (key === 'maxPrice') {
      setLocalMaxPrice('');
      delete currentQuery.maxPrice;
    } else if (key === 'bedrooms') {
      setLocalBedrooms('');
      delete currentQuery.bedrooms;
    } else if (key === 'bathrooms') {
      setLocalBathrooms('');
      delete currentQuery.bathrooms;
    } else if (key === 'minArea') {
      setLocalMinArea('');
      delete currentQuery.minArea;
    } else if (key === 'maxArea') {
      setLocalMaxArea('');
      delete currentQuery.maxArea;
    }

    // 4. Update URL
    router.push({
      pathname: router.pathname,
      query: currentQuery,
    }, undefined, { shallow: true });

    // 5. Fetch new results
    fetchData(currentQuery, false);
  };

  const handleClearFilters = () => {
    setLocalCitySelected(null);
    setLocalNeighbourhoodSelected(null);
    setLocalPropertyTypes([]);
    setLocalAmenities([]);
    setLocalMinPrice('');
    setLocalMaxPrice('');
    setLocalMinArea('');
    setLocalMaxArea('');
    setLocalBedrooms('');
    setLocalBathrooms('');
    setLocalOrderBy('date');

    // Close offcanvas on mobile
    handleClose();

    // Build query with cleared filters and trigger API call
    const clearedQuery = {
      page: 1,
      limit: LIMIT_PER_PAGE,
      category: localCategory !== 'vendre' ? localCategory : undefined
    };

    // Update URL
    router.push({
      pathname: router.pathname,
      query: clearedQuery,
    }, undefined, { shallow: true });

    // Fetch data with cleared filters
    fetchData(clearedQuery, false);
  };

  const propertyType = [
    { key: 'Maison', label: t('Maison') },
    { key: 'Villa', label: t('Villa') },
    { key: 'Appartement', label: t('Appartement') },
    { key: 'Immeuble', label: t('Immeuble') },
    { key: 'Terrain', label: t('Terrain') },
    { key: 'Parking', label: t('Parking') },
    { key: 'Local commercial', label: t('Local commercial') },
    { key: 'Bureau', label: t('Bureau') },
    { key: 'Studio', label: t('Studio') },
    { key: 'Ferme', label: t('Ferme') },
  ].map(item => ({ ...item, value: item.key }));

  const propertyCategory = [
    { label: t('Vente'), value: 'vendre' },
    { label: t('Location'), value: 'location' }
  ];

  const bedroomsList = [
    { name: t('Tout'), value: '' },
    { name: '1', value: '1' },
    { name: '2', value: '2' },
    { name: '3', value: '3' },
    { name: '4+', value: '4+' }
  ];

  const bathroomsList = [
    { name: t('Tout'), value: '' },
    { name: '1', value: '1' },
    { name: '2', value: '2' },
    { name: '3', value: '3' },
    { name: '4', value: '4' }
  ];

  const amenitiesList = [
    { key: 'Climatisation', label: t('Climatisation') },
    { key: 'Balcon', label: t('Balcon') },
    { key: 'Garage', label: t('Garage') },
    { key: 'Gym', label: t('Gym') },
    { key: 'Parking', label: t('Parking') },
    { key: 'Piscine', label: t('Piscine') },
    { key: 'Sécurité', label: t('Sécurité') },
    { key: 'WiFi', label: t('WiFi') },
    { key: 'Machine à laver', label: t('Machine à laver') },
    { key: 'Lave-vaisselle', label: t('Lave-vaisselle') }
  ].map(item => ({ ...item, value: item.key }));

  // --- Component Rendering ---
  return (
    <RealEstatePageLayout
      pageTitle={`${t('Biens à')} ${t(categoryParam === 'vendre' ? 'vendre' : 'louer')}`}
      activeNav='Catalog'
      // Filters, sorting and pages all point to the plain buy / rent catalog
      canonicalPath={`/catalog?category=${categoryParam}`}
      description={categoryParam === 'vendre'
        ? t("Biens immobiliers à vendre au Maroc : appartements, villas, maisons, terrains et locaux commerciaux. Filtrez par ville, quartier, prix et surface.")
        : t("Biens immobiliers à louer au Maroc : appartements, villas, bureaux et locaux commerciaux. Filtrez par ville, quartier, prix et surface.")}
    >
      <Container fluid className='mt-5 pt-5 p-0'>
        <Row className='g-0 mt-n3'>
          <Col
            as='aside'
            lg={4}
            xl={3}
            className='border-top-lg border-end-lg shadow-sm px-3 px-xl-4 px-xxl-5 pt-lg-2'
          >
            <Offcanvas
              show={show}
              onHide={handleClose}
              responsive='lg'
              enforceFocus={false}
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title as='h5'>{t('Filtrer')}</Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body className='px-0 py-0 d-flex flex-column'>
                {/* Location Dropdowns: outside overflow-auto so the panel isn't offset
                    by window.pageYOffset when Bootstrap locks body scroll on mobile */}
                <div className='pb-4 mb-2 pt-3 pt-lg-4 px-4 px-lg-0' style={{ position: 'relative', zIndex: 2 }}>
                  <h3 className='h6'>{t('Localisation')}</h3>
                  <div className={'mb-2'}>
                    <Dropdown
                      ref={refDropDownCity}
                      value={localCitySelected}
                      onChange={handleCity}
                      options={cities}
                      optionLabel='name'
                      placeholder={t('Ville')}
                      pt={{ input: { 'aria-label': t('Ville') }, select: { 'aria-label': t('Ville') } }}
                      filter
                      showClear
                      field={'name'}
                      className='w-full md:w-14rem'
                      appendTo="self"
                    />
                  </div>
                  <div>
                    <Dropdown
                      value={localNeighbourhoodSelected}
                      onChange={handleNeighbourhood}
                      options={neighbourhoodsByCity[localCitySelected?.id_city] || []}
                      optionLabel='name'
                      placeholder={t('Quartier')}
                      pt={{ input: { 'aria-label': t('Quartier') }, select: { 'aria-label': t('Quartier') } }}
                      filter
                      showClear
                      field={'name'}
                      className='w-full md:w-14rem'
                      disabled={!localCitySelected}
                      appendTo="self"
                    />
                  </div>
                </div>
                <div className={'pb-lg-4 px-4 px-lg-0 flex-grow-1 overflow-auto'}>
                  {/* Category Radio Buttons: Linked to local state */}
                  <div className='pb-4 mb-2'>
                    <h3 className='h6'>{t('Catégorie du bien')}</h3>
                    <SimpleBar autoHide={false} className='simplebar-no-autohide' style={{ maxHeight: '11rem' }}>
                      {propertyCategory.map(({ label, value }, indx) => (
                        <Form.Check
                          key={indx}
                          id={`category-${indx}`}
                          value={value}
                          checked={localCategory === value}
                          onChange={handleCategoryBien}
                          label={<><span className='fs-sm'>{label}</span></>}
                          type='radio'
                          name='category'
                        />
                      ))}
                    </SimpleBar>
                  </div>

                  {/* Property Type Checkboxes: Linked to local state */}
                  <div className='pb-4 mb-2'>
                    <h3 className='h6'>{t('Type de bien')}</h3>
                    <SimpleBar autoHide={false} className='simplebar-no-autohide'>
                      {propertyType.map(({ value, label }, indx) => (
                        <Form.Check
                          key={indx}
                          id={`type-${indx}`}
                          value={value}
                          checked={localPropertyTypes.includes(value)}
                          onChange={handlePropertyType}
                          label={<><span className='fs-sm'>{label}</span></>}
                          type='checkbox'
                        />
                      ))}
                    </SimpleBar>
                  </div>

                  {/* Price Inputs: Linked to local state */}
                  <div className='pb-4 mb-2'>
                    <h3 className='h6'>{t('Budget en DH')}</h3>
                    <div className='d-flex align-items-center'>
                      <div className='w-100 pe-2'>
                        <InputGroup>
                          <Form.Control
                            name='minPrice'
                            value={localMinPrice}
                            onChange={handleMinPrice}
                            type='text'
                            placeholder={t('Min')}
                          />
                        </InputGroup>
                      </div>
                      <div className='text-muted'>—</div>
                      <div className='w-100 ps-2'>
                        <InputGroup>
                          <Form.Control
                            name='maxPrice'
                            value={localMaxPrice}
                            onChange={handleMaxPrice}
                            type='text'
                            placeholder={t('Max')}
                          />
                        </InputGroup>
                      </div>
                    </div>
                    {/* Price validation message */}
                    {localMinPrice && localMaxPrice && parseInt(localMinPrice.replace(/\s/g, '')) > parseInt(localMaxPrice.replace(/\s/g, '')) && (
                      <div className="text-danger small mt-1">
                        {t('Le prix minimum ne peut pas être supérieur au prix maximum')}
                      </div>
                    )}
                  </div>

                  {/* Bedrooms & Bathrooms: Linked to local state */}
                  <div className='pb-4 mb-2'>
                    <h3 className='h6 pt-1'>{t('Chambres & salles de bain')}</h3>
                    <label className='d-block fs-sm mb-1'>{t('Chambres')}</label>
                    <ButtonGroup size='sm'>
                      {bedroomsList.map((bedroom, indx) => (
                        <ToggleButton
                          key={indx}
                          type='radio'
                          id={`bedrooms-${indx}`}
                          name='bedrooms'
                          value={bedroom.value}
                          checked={localBedrooms === bedroom.value}
                          onChange={handleBedrooms}
                          variant='outline-secondary fw-normal'
                        >
                          {bedroom.name}
                        </ToggleButton>
                      ))}
                    </ButtonGroup>
                    <label className='d-block fs-sm pt-2 my-1'>{t('Salles de bain')}</label>
                    <ButtonGroup size='sm'>
                      {bathroomsList.map((bathroom, indx) => (
                        <ToggleButton
                          key={indx}
                          type='radio'
                          id={`bathrooms-${indx}`}
                          name='bathrooms'
                          value={bathroom.value}
                          checked={localBathrooms === bathroom.value}
                          onChange={handleBathrooms}
                          variant='outline-secondary fw-normal'
                        >
                          {bathroom.name}
                        </ToggleButton>
                      ))}
                    </ButtonGroup>
                  </div>

                  {/* Area Inputs: Linked to local state */}
                  <div className='pb-4 mb-2'>
                    <h3 className='h6 pt-1'>{t('Surface en m²')}</h3>
                    <div className='d-flex align-items-center'>
                      <Form.Control
                        type='number'
                        value={localMinArea}
                        placeholder='Min'
                        onChange={handleMinArea}
                        className='w-100'
                      />
                      <div className='mx-2'>—</div>
                      <Form.Control
                        type='number'
                        value={localMaxArea}
                        placeholder='Max'
                        onChange={handleMaxArea}
                        className='w-100'
                      />
                    </div>
                  </div>

                  {/* Amenities Checkboxes: Linked to local state */}
                  <div className='pb-4 mb-2 d-none'>
                    <h3 className='h6'>{t('Commodités')}</h3>
                    <SimpleBar autoHide={false} className='simplebar-no-autohide' style={{ maxHeight: '11rem' }}>
                      {amenitiesList.map(({ value, label }, indx) => (
                        <Form.Check
                          key={indx}
                          id={`amenity-${indx}`}
                          value={value}
                          checked={localAmenities.includes(value)}
                          onChange={handleAmenities}
                          label={<><span className='fs-sm'>{label}</span></>}
                        />
                      ))}
                    </SimpleBar>
                  </div>
                </div>

                {/* --- Apply Filters Button --- */}
                <div className='border-top py-4 position-sticky bottom-0 mt-auto' style={{ backgroundColor: "var(--bs-body-bg)" }}>
                  <div className={'d-grid gap-2 px-4 px-lg-0'}>
                    <Button
                      variant='primary'
                      className='w-100'
                      onClick={handleApplyFilters}
                    >
                      {t('Appliquer les filtres')}
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        variant='outline-secondary'
                        onClick={handleClearFilters}
                      >
                        {t('Effacer les filtres')}
                      </Button>
                    )}
                  </div>
                </div>
              </Offcanvas.Body>
            </Offcanvas>
          </Col>
          <Col lg={8} xl={9} className='position-relative overflow-hidden pb-5 pt-4 px-3 px-xl-4 px-xxl-5'>
            {/* Map Popup (remains the same) */}
            <div className={`map-popup${showMap ? ' show' : ''}`}>
              <Button
                size='sm'
                variant='light btn-icon shadow-sm rounded-circle'
                onClick={() => setShowMap(false)}
              >
                <i className='fi-x fs-xs'></i>
              </Button>
            </div>

            {/* Breadcrumbs (remains the same) */}
            <Breadcrumb className='mb-3 pt-md-2'>
              <Breadcrumb.Item linkAs={Link} href='/'>{t('Accueil')}</Breadcrumb.Item>
              <Breadcrumb.Item active>{t('Biens à')} {t(categoryParam === 'vendre' ? 'vendre' : 'louer')}</Breadcrumb.Item>
            </Breadcrumb>
            <div className='d-sm-flex align-items-center justify-content-between pb-3 pb-sm-4'>
              <h1 className='h2 mb-sm-0'>{t('Biens à')} {t(categoryParam === 'vendre' ? 'vendre' : 'louer')}</h1>
            </div>

            {/* Active Filters Summary */}
            <ActiveFiltersSummary
              localCitySelected={localCitySelected}
              localNeighbourhoodSelected={localNeighbourhoodSelected}
              localPropertyTypes={localPropertyTypes}
              localMinPrice={localMinPrice}
              localMaxPrice={localMaxPrice}
              localBedrooms={localBedrooms}
              localBathrooms={localBathrooms}
              localMinArea={localMinArea}
              localMaxArea={localMaxArea}
              onClearFilters={handleClearFilters}
              onRemoveFilter={handleRemoveFilter}
              t={t}
            />

            {/* Sort Dropdown: Linked to local state */}
            <div className='d-flex flex-sm-row flex-column align-items-sm-center align-items-stretch my-2'>
              <Form.Group controlId='sortby' className='d-flex align-items-center flex-shrink-0'>
                <Form.Label className='text-body fs-sm me-2 mb-0 pe-1 text-nowrap'>
                  <i className='fi-arrows-sort text-muted mt-n1 me-2'></i>
                  {t('Trier par:')}
                </Form.Label>
                <Form.Select size='sm' onChange={handleOrderBy} value={localOrderBy}>
                  <option value='date'>{t('Plus récents')}</option>
                  <option value='price'>{t('Prix décroissant')}</option>
                  <option value='price-desc'>{t('Prix croissant')}</option>
                </Form.Select>
                <Button size='sm' variant='primary' className='ms-2' onClick={handleApplyFilters}>
                  {t('Trier')}
                </Button>
              </Form.Group>
              <hr className='d-none d-sm-block w-100 mx-4' />
              <div className='d-flex align-items-center flex-shrink-0 text-muted'>
                <i className='fi-check-circle me-2'></i>
                {loadingProperties ? <span>Chargement...</span> : (
                  <span className='fs-sm mt-n1'>
                    {properties.length > 0 ? (
                      <>
                        <strong>{properties.length}</strong> {t(`sur`)} <strong>{totalResults || properties.length}</strong> {t(`propriété${(totalResults || properties.length) !== 1 ? 's' : ''}`)}
                      </>
                    ) : (
                      t('Aucun résultat')
                    )}
                  </span>)}
              </div>
            </div>

            {/* Property Catalog Display */}
            {/* Keeps the heading order h1 > h2 > h3 (card titles) for screen readers */}
            <h2 className='visually-hidden'>{t('Annonces de biens')}</h2>
            <Row xs={1} sm={2} xl={3} className='g-4 py-4'>
              {loadingProperties && currentPage === 1 ? (
                // Enhanced skeleton loading
                Array.from({ length: 6 }).map((_, indx) => (
                  <Col key={indx}>
                    <SkeletonCard />
                  </Col>
                ))
              ) : properties.length === 0 ? (
                <Col xs={12}><NoData /></Col>
              ) : (
                properties.map((property, indx) => (
                  <Col key={property.id + '-' + indx}>
                    <PropertyCard
                      ribbon={property.ribbon}
                      href={listingPath(property.title, property.id)}
                      images={property.images}
                      thumbnail={property.thumbnail}
                      title={property.title}
                      category={property.category}
                      location={property.location}
                      price={property.price}
                      badges={property.badges}
                      footer={[
                        ['fi-bed', property.amenities[0]],
                        ['fi-bath', property.amenities[1]],
                        ['fi-parking', property.amenities[2]]
                      ]}
                      className='h-100'
                    />
                  </Col>
                ))
              )}
              {/* Show a single placeholder card if loading more pages */}
              {loadingProperties && currentPage > 1 && (
                <Col><LoadingCardCatalog /></Col>
              )}
            </Row>

            {/* --- Load More Button (Pagination) --- */}
            {properties.length > 0 && hasMore && (
              <div className='d-flex justify-content-center pt-4 mt-3'>
                <Button
                  variant='outline-primary'
                  onClick={handleLoadMore}
                  disabled={loadingProperties}
                >
                  {loadingProperties ? (
                    <>
                      <Spinner animation="border" size="sm" className='me-2' />
                      {t('Chargement...')}
                    </>
                  ) : (
                    t('Charger plus')
                  )}
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
      <Button size='sm' className='w-100 rounded-0 fixed-bottom d-lg-none ' onClick={handleShow}>
        <i className='fi-filter me-2'></i>
        {t('Filtres')}
        {activeFilterCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {activeFilterCount}
          </span>
        )}
      </Button>
    </RealEstatePageLayout>
  );
};

export default CatalogPage;

// Cities/neighbourhoods rarely change: keep them in memory instead of
// refetching on every catalog request.
const LOCATIONS_TTL_MS = 60 * 60 * 1000;
let locationsCache = { data: null, fetchedAt: 0 };

const getLocations = async () => {
  if (locationsCache.data && Date.now() - locationsCache.fetchedAt < LOCATIONS_TTL_MS) {
    return locationsCache.data;
  }
  const { data } = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/api/ads/localisation`);

  // Keep only what the filters use: the API also sends coordinates and
  // timestamps for ~1,800 neighbourhoods (~900 KB of page data)
  const cities = (data.cities || []).map(({ id_city, name }) => ({ id_city, name }));
  const trimNeighbourhood = ({ id_neighborhood, id_city, name }) => ({ id_neighborhood, id_city, name });
  const neighbourhoods_by_city = Object.fromEntries(
    Object.entries(data.neighbourhoods_by_city || {}).map(([cityId, list]) => [cityId, list.map(trimNeighbourhood)])
  );

  const trimmed = { cities, neighbourhoods_by_city };
  locationsCache = { data: trimmed, fetchedAt: Date.now() };
  return trimmed;
};

// --- getServerSideProps Implementation ---
export async function getServerSideProps({ query, res }) {
  // Whitelist: the category is interpolated into the API path
  const category = query.category === 'location' ? 'location' : 'vendre';
  let initialProperties = [];
  let initialLocations = {};
  let initialTotalResults = 0;
  const LIMIT_PER_PAGE = 9;

  const propertiesUrl = `${process.env.NEXT_PUBLIC_API_URL}/api/ads/properties/${category}`;
  const requestQuery = { ...query, page: query.page || 1, limit: LIMIT_PER_PAGE };

  // Fetch properties and localization data in parallel
  const [propertiesResult, locationsResult] = await Promise.allSettled([
    axios.post(propertiesUrl, requestQuery),
    getLocations(),
  ]);

  if (propertiesResult.status === 'fulfilled') {
    const { data } = propertiesResult.value;
    initialProperties = data.data?.properties || [];
    initialTotalResults = data.data?.pagination?.total || 0; // Get total from backend
  } else {
    console.error('SSR Error fetching properties:', propertiesResult.reason);
  }

  if (locationsResult.status === 'fulfilled') {
    initialLocations = locationsResult.value;
  } else {
    console.error('SSR Error fetching localization:', locationsResult.reason);
  }

  // Same listings for every visitor: let the CDN serve them for a minute
  res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300');

  return {
    props: {
      initialProperties,
      initialLocations,
      initialTotalResults
    }
  };
}
